import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  DwComponent,
  DwComponentType,
  DwEventService,
  DwExpressionService,
  DwGridEvents,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQueryHelper,
  DwSectionBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService,
  getParameterByName
} from '@devwareapps/devware-cap';
import { mergeMap } from 'rxjs/operators';

import {
  AIPromptRequestStatusAllItems,
  AppMetaData,
  AppMetaDataItemNames,
  LessonTypeAllItems,
  StudentCourseEntity,
  StudentCourseProgressByStudentCourseQueryEntity,
  StudentLessonProgressQueryEntity,
  StudentModuleProgressQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import { CourseProgressVariableContext } from '../../models/course-progress-variable-context.model';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { VideoModalService } from '../../../videos/services/video-modal.service';

interface CourseProgressTab {
  display: string;
  iconClass: string;
  value: string;
}

interface CourseProgressModuleViewModel {
  data: StudentModuleProgressQueryEntity;
  title: string;
  lessonsLabel: string;
  percentLabel: string;
  progressWidth: number;
  selected: boolean;
  completed: boolean;
  hasAiSummary: boolean;
}

interface CourseProgressHeaderViewModel {
  title: string;
  lessonsValue: string;
  lessonsSummary: string;
  runtimeLabel: string;
  runtimeSummary: string;
  progressLabel: string;
  progressWidth: number;
  completed: boolean;
}

interface CourseProgressLessonViewModel {
  data: StudentLessonProgressQueryEntity;
  title: string;
  statusLabel: string;
  statusClass: string;
  metaLabel: string;
  runtimeLabel: string;
  iconClass: string;
  iconLabel: string;
  isVideo: boolean;
}

@DwComponent({
  key: 'courseStudentProgressFormSectionRedesign',
  name: 'Course Student Progress - Form Section Redesign',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.StudentCourse,
  isGlobal: false,
})
@Component({
  selector: 'app-course-student-progress-redesign',
  templateUrl: './course-student-progress-redesign.component.html',
  styleUrls: ['./course-student-progress-redesign.component.scss']
})
export class CourseStudentProgressRedesignComponent extends DwSectionBaseComponent implements OnInit {
  formApi: DwMetaDataFormApi;
  studentCourseProgress: StudentCourseProgressByStudentCourseQueryEntity;
  studentCourse: StudentCourseEntity;
  courseModuleProgress: StudentModuleProgressQueryEntity[] = [];
  currentModule: StudentModuleProgressQueryEntity;
  initComplete: boolean;
  loadingLessons: boolean;

  moduleViewModels: CourseProgressModuleViewModel[] = [];
  selectedModuleViewModel: CourseProgressModuleViewModel;
  headerViewModel: CourseProgressHeaderViewModel;
  lessonViewModels: CourseProgressLessonViewModel[] = [];
  readonly lessonSkeletonRows = [0, 1, 2];
  tabs: CourseProgressTab[] = [];
  currentTab = 'lessons';
  mobileDetailVisible: boolean;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) protected dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseRepositoryService: CourseRepositoryService,
    private dwEventService: DwEventService,
    private router: Router,
    private videoModalService: VideoModalService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    this.setupSection();
  }

  openModule(moduleProgress: StudentModuleProgressQueryEntity): void {
    this.mobileDetailVisible = true;
    this.selectModule(moduleProgress);
  }

  returnToModules(): void {
    this.mobileDetailVisible = false;
  }

  selectModule(moduleProgress: StudentModuleProgressQueryEntity): void {
    if (!moduleProgress) {
      return;
    }

    this.currentModule = moduleProgress;
    this.formApi.patchVariableContext<CourseProgressVariableContext>({
      Selected_Module_Id: moduleProgress.CourseModuleProgressId
    });

    this.setupTabs(moduleProgress);
    this.updateModuleViewModels();
    this.headerViewModel = this.createHeaderViewModel(moduleProgress);
    this.loadLessons(moduleProgress.CourseModuleProgressId);
  }

  setTab(tabValue: string): void {
    this.currentTab = tabValue;
  }

  clickLesson(lessonViewModel: CourseProgressLessonViewModel): void {
    this.formApi.patchVariableContext<CourseProgressVariableContext>({
      Tab_Index: 2
    });

    this.dwEventService.publishEvent(DwGridEvents.setQuickSearchText, {
      itemName: AppMetaDataItemNames.CourseModuleProgress,
      quickSearchText: lessonViewModel.data.CourseLessonLessonName
    });
  }

  showVideo(lessonViewModel: CourseProgressLessonViewModel, event: MouseEvent): void {
    if (!lessonViewModel.isVideo) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.videoModalService.showVideoModal(
      lessonViewModel.data.CourseLessonVideoId,
      lessonViewModel.data.CourseLessonLessonName
    );
  }

  trackByModuleId(index: number, moduleViewModel: CourseProgressModuleViewModel): number {
    return moduleViewModel.data.CourseModuleProgressId || index;
  }

  trackByLessonId(index: number, lessonViewModel: CourseProgressLessonViewModel): number {
    return lessonViewModel.data.CourseLessonProgressId || index;
  }

  private setupSection(): void {
    this.studentCourse = this.formApi.getFormData<StudentCourseEntity>();

    this.courseRepositoryService.getStudentCourseProgressByStudentCourse(this.studentCourse?.StudentCourseId)
      .subscribe(studentCourseProgress => {
        this.studentCourseProgress = studentCourseProgress;
        this.studentCourse = studentCourseProgress;
        this.courseModuleProgress = (this.studentCourseProgress?.CourseModuleProgress || []) as StudentModuleProgressQueryEntity[];

        const currentModule = this.getInitialModule();
        this.selectModule(currentModule);
        this.initComplete = true;
      });
  }

  private getInitialModule(): StudentModuleProgressQueryEntity {
    let currentModule = this.courseModuleProgress[0];
    const courseModuleId = getParameterByName(this.router, 'CourseModuleId');

    if (courseModuleId) {
      currentModule = this.courseModuleProgress.find(m => m.CourseModuleProgressId === parseInt(courseModuleId, 10)) || currentModule;
    }

    return currentModule;
  }

  private setupTabs(module: StudentModuleProgressQueryEntity): void {
    this.tabs = [
      { display: 'Lessons', iconClass: '', value: 'lessons' }
    ];

    if (module?.AIPromptRequestAIPromptRequestStatusId === AIPromptRequestStatusAllItems.Success) {
      this.tabs.push({ display: 'AI summary', iconClass: 'fa fa-magic dw-purple', value: 'ai-summary' });
    }

    if (!this.tabs.some(tab => tab.value === this.currentTab)) {
      this.currentTab = this.tabs[0].value;
    }
  }

  private loadLessons(courseModuleProgressId: number): void {
    const requestedCourseModuleProgressId = courseModuleProgressId || -1;

    this.loadingLessons = true;
    this.lessonViewModels = [];

    AppMetaData.Queries.StudentLessonProgress.CreateQueryBuilder(this.dwMetaDataService)
      .pipe(mergeMap(query => {
        const queryHelper = new DwQueryHelper();
        const clonedQuery = queryHelper.cloneQuery(query.query);

        query = AppMetaData.Queries.StudentLessonProgress.CreateQueryBuilderFromQuery(clonedQuery);
        query.addFilterAnd(f => f.Equal(a => a.CourseModuleProgressId, requestedCourseModuleProgressId));
        query.addOrderBy(o => o.CourseLessonLessonOrder);

        return this.dwOrmDataService.executeQuery(query.query);
      }))
      .subscribe(lessons => {
        if (this.currentModule?.CourseModuleProgressId !== requestedCourseModuleProgressId) {
          return;
        }

        const lessonItems = (lessons || []) as StudentLessonProgressQueryEntity[];

        this.lessonViewModels = lessonItems.map(lesson => this.createLessonViewModel(lesson));
        this.loadingLessons = false;
      }, () => {
        if (this.currentModule?.CourseModuleProgressId !== requestedCourseModuleProgressId) {
          return;
        }

        this.lessonViewModels = [];
        this.loadingLessons = false;
      });
  }

  private updateModuleViewModels(): void {
    this.moduleViewModels = (this.courseModuleProgress || []).map(module => this.createModuleViewModel(module));
    this.selectedModuleViewModel = this.moduleViewModels.find(module => module.data.CourseModuleProgressId === this.currentModule?.CourseModuleProgressId);
  }

  private createModuleViewModel(module: StudentModuleProgressQueryEntity): CourseProgressModuleViewModel {
    const percent = this.normalizePercent(module.PercentComplete);
    const lessonsCompleted = module.LessonsCompleted || 0;
    const lessonsCount = module.LessonsCount || 0;

    return {
      data: module,
      title: module.CourseModuleModuleName || module.CourseModuleIdDisplay || 'Module',
      lessonsLabel: `${lessonsCompleted}/${lessonsCount} lessons`,
      percentLabel: `${percent}%`,
      progressWidth: percent,
      selected: module.CourseModuleProgressId === this.currentModule?.CourseModuleProgressId,
      completed: percent >= 100,
      hasAiSummary: module.AIPromptRequestAIPromptRequestStatusId === AIPromptRequestStatusAllItems.Success
    };
  }

  private createHeaderViewModel(module: StudentModuleProgressQueryEntity): CourseProgressHeaderViewModel {
    const percent = this.normalizePercent(module.PercentComplete);
    const lessonsCompleted = module.LessonsCompleted || 0;
    const lessonsCount = module.LessonsCount || 0;
    const moduleTitle = module.CourseModuleModuleName || module.CourseModuleIdDisplay || 'Module';

    return {
      title: moduleTitle,
      lessonsValue: `${lessonsCompleted}/${lessonsCount}`,
      lessonsSummary: `${lessonsCompleted} completed`,
      runtimeLabel: this.formatDurationHourMinutes(module.TotalTimeSeconds),
      runtimeSummary: `Across ${lessonsCount} lessons`,
      progressLabel: `${percent}%`,
      progressWidth: percent,
      completed: percent >= 100
    };
  }

  private createLessonViewModel(lesson: StudentLessonProgressQueryEntity): CourseProgressLessonViewModel {
    const completed = lesson.LessonComplete || this.normalizePercent(lesson.PercentComplete) >= 100;
    const hasProgress = completed || this.normalizePercent(lesson.PercentComplete) > 0 || !!lesson.LastActivityDateTime;

    return {
      data: lesson,
      title: lesson.CourseLessonLessonName || lesson.CourseLessonIdDisplay || 'Lesson',
      statusLabel: completed ? 'Completed' : hasProgress ? 'In progress' : 'Not started',
      statusClass: completed ? 'is-completed' : hasProgress ? 'is-active' : 'is-muted',
      metaLabel: this.formatLessonMeta(lesson, completed, hasProgress),
      runtimeLabel: this.formatDurationHourMinutes(lesson.TotalTimeSeconds),
      ...this.getLessonIcon(lesson.CourseLessonLessonTypeId)
    };
  }

  private getLessonIcon(lessonTypeId: number): Pick<CourseProgressLessonViewModel, 'iconClass' | 'iconLabel' | 'isVideo'> {
    switch (lessonTypeId) {
      case LessonTypeAllItems.Quiz:
        return { iconClass: 'fa fa-list-alt', iconLabel: 'Quiz lesson', isVideo: false };
      case LessonTypeAllItems.ProTools:
        return { iconClass: 'fa fa-cube', iconLabel: '3D lesson', isVideo: false };
      case LessonTypeAllItems.Video:
      default:
        return { iconClass: 'fa fa-play', iconLabel: 'View video', isVideo: true };
    }
  }

  private formatLessonMeta(lesson: StudentLessonProgressQueryEntity, completed: boolean, hasProgress: boolean): string {
    if (completed && lesson.CompletedDateTime) {
      return this.formatDateTime(lesson.CompletedDateTime);
    }

    if (hasProgress && lesson.LastActivityDateTime) {
      return `Last activity ${this.formatDateTime(lesson.LastActivityDateTime)}`;
    }

    return 'No activity yet';
  }

  private formatDateTime(value: string): string {
    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '';
    }

    const dateText = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeText = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    return `${dateText} ${timeText}`;
  }

  private formatDurationHourMinutes(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds || 0));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }

    if (hours > 0) {
      return `${hours}h`;
    }

    return `${remainingMinutes}m`;
  }

  private normalizePercent(value: number): number {
    return Math.max(0, Math.min(100, Math.round(value || 0)));
  }

}
